import { ArrowRight, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api, isApiError } from '@/api';
import { Callout, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { CopyText, EmptyState, Loading } from '@/components/States';
import { useRetained } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { DueCell, EnumBadge, Timeline, useEnumLabel } from './ui';
import { ABUSE_STATUS, LE_STATUS, PRIORITY } from './vocab';

/**
 * `/compliance/law-enforcement-requests/:id`: one request from an authority, with the abuse
 * case it belongs to. Registrant data never appears here.
 */
export function LawEnforcementRequestPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { id = '' } = useParams();
  const result = useApi((signal) => api.getLawEnforcementRequest(id, { signal }), [id]);
  const r = useRetained(result.status === 'success' ? result.data : undefined);
  useDocumentTitle(r ? `${r.agency} ${r.reference}` : t('leRequest.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('crumbs.icann') },
    { label: t('abuse.list.title'), to: '/compliance/icann/abuse' },
    ...(r ? [{ label: r.case.case_number, to: `/compliance/icann/abuse/${encodeURIComponent(r.case.id)}?tab=actions` }] : []),
    { label: r?.reference ?? '…' },
  ];

  if (!r) {
    return (
      <>
        <PageHeader title={t('leRequest.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError
            error={result.error}
            onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload}
            title={t('leRequest.loadError')}
          />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const casePath = `/compliance/icann/abuse/${encodeURIComponent(r.case.id)}`;

  return (
    <>
      <PageHeader
        title={r.agency}
        crumbs={crumbs}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <EnumBadge group="leStatus" value={r.status} tones={LE_STATUS} />
            <code className="text-xs">{r.reference}</code>
            <span aria-hidden="true">·</span>
            <EntityRef label={r.case.domain_name} to={`/domains/${encodeURIComponent(r.case.domain_name)}`} id={r.case.domain_id} />
          </span>
        }
        actions={
          <>
            <Button asChild size="sm" variant="ghost">
              <Link to={`${casePath}?tab=actions`}>
                {t('leRequest.updateOnCase')} <ArrowRight />
              </Link>
            </Button>
            <Button size="sm" variant="outline" onClick={result.reload}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
          </>
        }
      />
      <div className="space-y-4">
        {r.overdue && r.deadline_at ? (
          <Callout tone="crit" title={t('sla.overdue')}>
            {t('leRequest.overdue', { when: formatRelative(r.deadline_at) })}
          </Callout>
        ) : null}
        <Callout tone="info">{t('leRequest.privacy')}</Callout>

        <Section title={t('leRequest.facts')}>
          <KeyValue
            columns={3}
            rows={[
              [t('leRequest.agency'), r.agency],
              [t('leRequest.jurisdiction'), r.jurisdiction],
              [t('leRequest.reference'), <code key="ref">{r.reference}</code>],
              [t('leRequest.legalBasis'), r.legal_basis],
              [t('leRequest.received'), formatDateTime(r.received_at)],
              [
                t('leRequest.deadline'),
                r.deadline_at ? <DueCell key="dl" dueAt={r.deadline_at} overdue={r.overdue} done={r.responded_at ? formatDateTime(r.deadline_at) : undefined} /> : null,
              ],
              [t('leRequest.status'), <EnumBadge key="st" group="leStatus" value={r.status} tones={LE_STATUS} />],
              [t('leRequest.respondedAt'), r.responded_at ? formatDateTime(r.responded_at) : null],
              [t('leRequest.recordedBy'), <EntityRef key="rb" label={r.recorded_by_display_name ?? label('actorType', r.recorded_by_type)} id={r.recorded_by_id} />],
              [t('leRequest.response'), r.response_summary],
              [t('leRequest.id'), <CopyText key="id" value={r.id} />],
            ]}
          />
        </Section>

        <Section title={t('leRequest.case')}>
          <KeyValue
            columns={3}
            rows={[
              [t('leRequest.case'), <EntityRef key="c" label={r.case.case_number} to={casePath} id={r.case.id} />],
              [t('common.status'), <EnumBadge key="cs" group="abuseStatus" value={r.case.status} tones={ABUSE_STATUS} />],
              [t('abuse.list.col.category'), <span key="cat" title={r.case.category}>{label('abuseCategory', r.case.category)}</span>],
              [t('abuse.list.col.priority'), <EnumBadge key="p" group="priority" value={r.case.priority} tones={PRIORITY} />],
              [
                t('leRequest.domain'),
                <EntityRef key="d" label={r.case.domain_name} to={`/domains/${encodeURIComponent(r.case.domain_name)}`} id={r.case.domain_id} />,
              ],
              [t('abuse.detail.opened'), formatDateTime(r.case.opened_at)],
            ]}
          />
        </Section>

        <Section title={t('leRequest.history')}>
          {r.history.length === 0 ? (
            <EmptyState title={t('leRequest.noHistory')} />
          ) : (
            <Timeline
              items={[...r.history].reverse().map((h) => ({
                key: h.id,
                at: h.created_at,
                title: (
                  <span className="font-medium" title={h.action}>
                    {label('caseAction', h.action)}
                  </span>
                ),
                meta: <EntityRef label={h.actor_display_name ?? label('actorType', h.actor_type)} id={h.actor_id} />,
              }))}
            />
          )}
        </Section>
      </div>
    </>
  );
}
